// import React, { useState } from "react";
import React, { useState } from "react";
import { usePaginated,usePaginatedtrips, useAsync } from "../hooks";
import { api } from "../api";
import {
  useToast,
  useConfirm,
  Btn,
  Card,
  Spinner,
  ApiError,
  Modal,
  Pages,
  Inp,
  Sel,
  Txa,
  Empty
} from "../ui";

import InvoicePrintView from "./InvoicePrintView";
import PaymentInfoPicker from "../PaymentInfoPicker";
import ClientTripSelect, { tripDateRange } from "../ClientTripSelect";

import { BRAND } from "../brand";

// ─── STATUS CONFIG ────────────────────────────────────────────────────────────
const INV_STATUSES = ["all", "draft", "sent", "paid", "overdue", "cancelled"];
const INV_STATUS_COLORS = {
  draft:     "bg-slate-100 text-slate-600",
  sent:      "bg-blue-100 text-blue-700",
  paid:      "bg-emerald-100 text-emerald-700",
  overdue:   "bg-rose-100 text-rose-700",
  cancelled: "bg-slate-100 text-slate-400",
};

const fmtMoney = (n, cur = "USD") =>
  n != null ? new Intl.NumberFormat("en-US", { style: "currency", currency: cur, minimumFractionDigits: 2 }).format(n) : "–";

// ─── CURRENCY OPTIONS ─────────────────────────────────────────────────────────
const CURRENCIES = [
  { value: "USD", label: "USD – US Dollar" },
  { value: "NGN", label: "NGN – Nigerian Naira" },
  { value: "GBP", label: "GBP – British Pound" },
  { value: "EUR", label: "EUR – Euro" },
  { value: "AED", label: "AED – UAE Dirham" },
  { value: "GHS", label: "GHS – Ghanaian Cedi" },
  { value: "KES", label: "KES – Kenyan Shilling" },
  { value: "ZAR", label: "ZAR – South African Rand" },
  { value: "CAD", label: "CAD – Canadian Dollar" },
  { value: "AUD", label: "AUD – Australian Dollar" },
];

// ─── COUNTRY OPTIONS ──────────────────────────────────────────────────────────
const COUNTRIES = [
  { value: "",   label: "Select country (optional)" },
  { value: "Nigeria",              label: "🇳🇬 Nigeria" },
  { value: "Canada",               label: "🇨🇦 Canada" },
];

// ─── INVOICE ITEM EDITOR ──────────────────────────────────────────────────────
function ItemsEditor({ items, onChange, currency = "USD" }) {
  const addRow    = () => onChange([...items, { description: "", quantity: 1, unitPrice: 0 }]);
  const removeRow = (i) => onChange(items.filter((_, idx) => idx !== i));
  const setRow    = (i, k, v) =>
    onChange(items.map((row, idx) => idx === i ? { ...row, [k]: k === "description" ? v : +v || 0 } : row));
  const subtotal  = items.reduce((s, r) => s + r.quantity * r.unitPrice, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Line Items *</label>
        <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded-lg font-semibold">{currency}</span>
      </div>

      <div className="space-y-2">
        {items.map((row, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 bg-white hover:border-slate-300 transition-colors group">
            <div className="mb-3">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">Description</label>
              <textarea
                rows={3}
                placeholder={`e.g. Dubai Return Flight (Emirates) – 2 Adults\nDeparture: 15 Jun 2025 · Return: 30 Jun 2025\nIncludes checked baggage`}
                value={row.description}
                onChange={e => setRow(i, "description", e.target.value)}
                required
                className="w-full px-3 py-2 text-sm text-slate-700 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] resize-y transition-colors placeholder:text-slate-300 leading-relaxed"
              />
            </div>

            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-3">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">Qty</label>
                <input type="number" min="0"
                  className="w-full px-2.5 py-2 text-sm text-slate-700 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] text-center"
                  value={row.quantity} onChange={e => setRow(i, "quantity", e.target.value)} />
              </div>
              <div className="col-span-4">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">Unit Price ({currency})</label>
                <input type="number" min="0" step="0.01"
                  className="w-full px-2.5 py-2 text-sm text-slate-700 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] text-right"
                  value={row.unitPrice} onChange={e => setRow(i, "unitPrice", e.target.value)} />
              </div>
              <div className="col-span-4">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">Line Total</label>
                <div className="w-full px-2.5 py-2 text-sm font-bold text-slate-700 rounded-lg bg-slate-50 border border-slate-200 text-right">
                  {fmtMoney(row.quantity * row.unitPrice, currency)}
                </div>
              </div>
              <div className="col-span-1 flex justify-center pb-1">
                <button type="button" onClick={() => removeRow(i)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-300 hover:text-rose-500 hover:bg-rose-50 transition-colors">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-1">
        <button type="button" onClick={addRow}
          className="text-xs font-semibold text-[rgb(var(--brand-rgb))] hover:text-[rgb(var(--brand-dark-rgb))] flex items-center gap-1.5 transition-colors px-3 py-2 rounded-lg hover:bg-[rgb(var(--brand-rgb)/0.05)]">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 4v16m8-8H4" /></svg>
          Add Line Item
        </button>
        <div className="text-right">
          <p className="text-xs text-slate-400 mb-0.5">Subtotal</p>
          <p className="text-base font-bold text-slate-800">{fmtMoney(subtotal, currency)}</p>
        </div>
      </div>
    </div>
  );
}

// ─── INVOICE FORM (create / edit) ─────────────────────────────────────────────
function InvoiceForm({ initial = {}, clients = [], onSubmit, onClose, saving }) {
  const [f, sf] = useState({
    invoiceReference:    initial.invoiceReference    || "",
    client:              initial.client?._id || initial.client || "",
    clientName:          initial.clientName          || "",
    email:               initial.email               || "",
    phone:               initial.phone               || "",
    country:             initial.country             || "",
    destination:         initial.destination         || "",
    tripDates:           initial.tripDates           || "",
    currency:            initial.currency            || BRAND.currency,
    paystackPaymentLink: initial.paystackPaymentLink || "",
    notes:               initial.notes               || "",
    status:              initial.status              || "draft",
    tripIndex:           "",   // which of the client's trips this is for (form-only, not saved)
    bankPaymentInfo: {
      accountNumber: initial.bankPaymentInfo?.accountNumber || "",
      accountName:   initial.bankPaymentInfo?.accountName   || "",
      bankName:      initial.bankPaymentInfo?.bankName      || "",
    },
    items: initial.items?.length
      ? initial.items.map(i => ({ description: i.description, quantity: i.quantity, unitPrice: i.unitPrice }))
      : [{ description: "", quantity: 1, unitPrice: 0 }],
  });

  const set  = k => e => sf(p => ({ ...p, [k]: e.target.value }));
  const setB = k => e => sf(p => ({ ...p, bankPaymentInfo: { ...p.bankPaymentInfo, [k]: e.target.value } }));

  const selectedClient = (clients || []).find(x => x._id === f.client);

  // Fill trip details from one of the client's trips. Line items are only filled when
  // they're still blank, so nothing typed is ever overwritten.
  const applyTrip = (p, c, idx) => {
    const t = c?.trips?.[idx];
    if (!t) return { ...p, tripIndex: "" };
    const dest  = (t.destinations || []).join(", ");
    const total = t.payment?.totalTripCost;
    const blank = p.items.length === 1 && !p.items[0].description.trim() && !Number(p.items[0].unitPrice);
    return {
      ...p,
      tripIndex:   String(idx),
      destination: dest || p.destination,
      tripDates:   tripDateRange(t) || p.tripDates,
      currency:    t.payment?.currency || p.currency,
      items: blank && total
        ? [{ description: `${t.tripType === "visa" ? "Visa processing" : "Travel package"} – ${dest}`, quantity: 1, unitPrice: total }]
        : p.items,
    };
  };

  const handleClientSelect = e => {
    const id = e.target.value;
    const c  = (clients || []).find(x => x._id === id);
    sf(p => applyTrip({
      ...p,
      client:     id,
      clientName: c?.fullName || p.clientName,
      email:      c?.email    || p.email,
      phone:      c?.phone    || p.phone,
    }, c, c?.trips?.length ? c.trips.length - 1 : -1)); // newest trip by default
  };

  const submit = e => {
    e.preventDefault();
    if (!f.clientName.trim() || !f.email.trim()) { alert("Client name and email are required"); return; }
    const { tripIndex, ...fields } = f; // tripIndex is only used by the form
    onSubmit({
      ...fields,
      invoiceReference:    f.invoiceReference.trim() || undefined, // blank → auto-generated
      // No client picked → send nothing (an empty value is not a valid client link).
      client:              f.client || undefined,
      country:             f.country || undefined,
      items:               f.items.filter(i => i.description.trim()),
      bankPaymentInfo:     Object.values(f.bankPaymentInfo).some(Boolean) ? f.bankPaymentInfo : undefined,
      paystackPaymentLink: f.paystackPaymentLink || undefined,
    });
  };

  const [tab, setTab] = useState("details");

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
        {["details", "items", "payment", "notes"].map(t => (
          <button key={t} type="button" onClick={() => setTab(t)}
            className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize ${tab === t ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "details" && (
        <div className="grid grid-cols-2 gap-4">
          {/* Invoice Reference */}
          <div className="col-span-2">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">
              Invoice Reference{" "}
              <span className="font-normal text-slate-400 normal-case">(auto-generated if blank)</span>
            </label>
            <input
              type="text"
              placeholder="Leave blank to auto-generate"
              value={f.invoiceReference}
              onChange={set("invoiceReference")}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
            />
            <p className="mt-1 text-[10px] text-slate-500">
              Must be unique. Blank uses the company prefix and next number, e.g. RLR-{new Date().getFullYear()}0001.
            </p>
          </div>

          {/* Client selector */}
          <div className="col-span-2">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Client</label>
            <select value={f.client} onChange={handleClientSelect}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]">
              <option value="">Select client (optional — or type below)</option>
              {(clients || []).map(c => <option key={c._id} value={c._id}>{c.fullName}</option>)}
            </select>
          </div>

          {/* Which trip — fills destination, dates, currency (and blank line items) */}
          {selectedClient?.trips?.length > 0 && (
            <div className="col-span-2">
              <ClientTripSelect
                client={selectedClient}
                value={f.tripIndex}
                onChange={idx => sf(p => applyTrip(p, selectedClient, idx))}
              />
            </div>
          )}

          <Inp label="Client Name *"  placeholder="Jane Doe"                      value={f.clientName}  onChange={set("clientName")}  required />
          <Inp label="Email *"        type="email" placeholder="jane@example.com" value={f.email}       onChange={set("email")}       required />
          <Inp label="Phone"          placeholder="+234..."                        value={f.phone}       onChange={set("phone")} />

          {/* Country */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Country</label>
            <select
              value={f.country}
              onChange={set("country")}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
            >
              {COUNTRIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <Inp label="Destination"  placeholder="Dubai, Abu Dhabi"       value={f.destination} onChange={set("destination")} />
          <Inp label="Trip Dates"   placeholder="15 Jun – 30 Jun 2025"   value={f.tripDates}   onChange={set("tripDates")}   className="col-span-2" />

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Currency *</label>
            <select value={f.currency} onChange={set("currency")}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]">
              {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          <Sel label="Status" value={f.status} onChange={set("status")} options={[
            { value: "draft",     label: "Draft"     },
            { value: "sent",      label: "Sent"      },
            { value: "paid",      label: "Paid"      },
            { value: "overdue",   label: "Overdue"   },
            { value: "cancelled", label: "Cancelled" },
          ]} />
        </div>
      )}

      {tab === "items" && (
        <ItemsEditor items={f.items} currency={f.currency} onChange={items => sf(p => ({ ...p, items }))} />
      )}

      {tab === "payment" && (
        <div className="space-y-4">
          {/* Saved bank accounts / payment links — fills the fields below (still editable) */}
          <PaymentInfoPicker
            bank={f.bankPaymentInfo}
            link={f.paystackPaymentLink}
            currency={f.currency}
            onChange={({ bank, link }) => sf(p => ({ ...p, bankPaymentInfo: bank, paystackPaymentLink: link }))}
          />
          <Inp label="Paystack Payment Link (optional)" placeholder="https://paystack.com/pay/…" value={f.paystackPaymentLink} onChange={set("paystackPaymentLink")} />
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider border-t border-slate-100 pt-3">
            Bank Transfer Details (optional)
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Inp label="Account Number" placeholder="0123456789"          value={f.bankPaymentInfo.accountNumber} onChange={setB("accountNumber")} />
            <Inp label="Account Name"   placeholder={`${BRAND.name} Agency Ltd`}  value={f.bankPaymentInfo.accountName}   onChange={setB("accountName")} />
            <Inp label="Bank Name"      placeholder="First Bank Nigeria"   value={f.bankPaymentInfo.bankName}      onChange={setB("bankName")}      className="col-span-2" />
          </div>
        </div>
      )}

      {tab === "notes" && (
        <Txa label="Notes" placeholder="e.g. Payment is due 7 days before travel. All prices are in USD." value={f.notes} onChange={set("notes")} rows={6} />
      )}

      <div className="flex gap-3 pt-2 border-t border-slate-100">
        <Btn type="submit" variant="primary" className="flex-1" loading={saving}>
          {initial._id ? "Update Invoice" : "Create Invoice"}
        </Btn>
        <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </form>
  );
}

// ─── SEND / REMINDER MODAL ────────────────────────────────────────────────────
function SendModal({ invoice, mode, onSend, onClose, saving }) {
  const isReminder = mode === "remind";
  const defaultSubject = isReminder
    ? `Payment Reminder – Invoice ${invoice.invoiceReference}`
    : `Your Invoice ${invoice.invoiceReference} from ${BRAND.shortName}`;
  const defaultBody = isReminder
    ? `Dear ${invoice.clientName},\n\nThis is a friendly reminder that your invoice ${invoice.invoiceReference} for ${fmtMoney(invoice.total, invoice.currency)} is still outstanding.\n\nPlease make payment at your earliest convenience.\n\nThank you,\n${BRAND.shortName} Team`
    : `Dear ${invoice.clientName},\n\nPlease find your invoice ${invoice.invoiceReference} for your upcoming trip to ${invoice.destination || "your destination"}.\n\nTrip Dates: ${invoice.tripDates || "TBC"}\nTotal Amount: ${fmtMoney(invoice.total, invoice.currency)}\n\n${invoice.paystackPaymentLink ? `Pay online: ${invoice.paystackPaymentLink}\n\n` : ""}${invoice.bankPaymentInfo?.accountNumber ? `Bank Transfer:\nAccount: ${invoice.bankPaymentInfo.accountNumber}\nName: ${invoice.bankPaymentInfo.accountName}\nBank: ${invoice.bankPaymentInfo.bankName}\n\n` : ""}${invoice.notes ? `Notes: ${invoice.notes}\n\n` : ""}Thank you for choosing ${BRAND.shortName}!\n\nWarm Regards,\n${BRAND.shortName} Customer Support Team`;

  const [subject, setSubject] = useState(defaultSubject);
  const [body,    setBody]    = useState(defaultBody);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-700">{isReminder ? "Send Payment Reminder" : "Send Invoice"}</p>
          <p className="text-xs text-slate-400">To: {invoice.email}</p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Subject</label>
          <input value={subject} onChange={e => setSubject(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wider">Message Body</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={10}
            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))] resize-none font-mono" />
        </div>
      </div>

      <div className="flex gap-3 pt-2 border-t border-slate-100">
        <Btn variant="primary" className="flex-1" loading={saving} onClick={() => onSend({ subject, body })}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          {isReminder ? "Send Reminder" : "Send Invoice"}
        </Btn>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </div>
  );
}

// ─── INVOICE DETAIL SLIDE-OVER ────────────────────────────────────────────────
function InvoiceDetailPanel({ invoice, onClose, onEdit, onStatusChange, onSend, onRemind, actionSaving }) {
  const [printMode, setPrintMode] = useState(false);

  const handlePrint = () => {
    const printContents = document.getElementById("invoice-print-root")?.innerHTML;
    if (!printContents) { setPrintMode(true); setTimeout(() => window.print(), 300); return; }
    window.print();
  };

  const handleDownload = () => {
    setPrintMode(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintMode(false), 500);
    }, 300);
  };

  if (printMode) return <InvoicePrintView invoice={invoice} />;

  const subtotal = (invoice.items || []).reduce((s, i) => s + i.quantity * i.unitPrice, 0);

  // Find the flag emoji for the country
  const countryInfo = COUNTRIES.find(c => c.value === invoice.country);
  const countryDisplay = countryInfo?.value ? countryInfo.label : invoice.country || null;

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-5 pb-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex-shrink-0">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Invoice</p>
            <p className="text-xl font-bold text-slate-800" style={{ fontFamily: "var(--font-heading)" }}>
              {invoice.invoiceReference}
            </p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${INV_STATUS_COLORS[invoice.status] || "bg-slate-100 text-slate-500"}`}>
                {invoice.status}
              </span>
              {countryDisplay && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                  {countryDisplay}
                </span>
              )}
              <span className="text-xs text-slate-400">
                {new Date(invoice.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
              </span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-[rgb(var(--brand-rgb))]">{fmtMoney(invoice.total ?? subtotal, invoice.currency)}</p>
            <p className="text-xs text-slate-400">{(invoice.items || []).length} item{invoice.items?.length !== 1 ? "s" : ""} · {invoice.currency || "USD"}</p>
          </div>
        </div>

        <div className="flex gap-2 mt-4 flex-wrap">
          {invoice.status !== "paid" && (
            <Btn variant="success" size="sm" loading={actionSaving} onClick={() => onStatusChange("paid")}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7" /></svg>
              Mark Paid
            </Btn>
          )}
          <Btn variant="secondary" size="sm" onClick={onSend}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            Send
          </Btn>
          <Btn variant="secondary" size="sm" onClick={onRemind}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5" /></svg>
            Reminder
          </Btn>
          <Btn variant="ghost" size="sm" onClick={onEdit}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            Edit
          </Btn>
          <Btn variant="ghost" size="sm" onClick={handleDownload}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>
            Download
          </Btn>
          <Btn variant="ghost" size="sm" onClick={handlePrint}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm1-4h.01" /></svg>
            Print
          </Btn>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

        {/* Client + Trip + Country info */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-xl">
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Client</p>
            <p className="font-bold text-slate-800">{invoice.clientName}</p>
            <p className="text-sm text-slate-500">{invoice.email}</p>
            {invoice.phone && <p className="text-sm text-slate-500">{invoice.phone}</p>}
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Trip</p>
            {invoice.destination && <p className="text-sm font-semibold text-slate-700">{invoice.destination}</p>}
            {invoice.tripDates   && <p className="text-sm text-slate-500">{invoice.tripDates}</p>}
            {countryDisplay      && (
              <p className="text-sm text-slate-500 mt-0.5">{countryDisplay}</p>
            )}
          </div>
        </div>

        {/* Line items */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Line Items</p>
            {invoice.currency && invoice.currency !== BRAND.currency && (
              <span className="text-[10px] font-bold text-[rgb(var(--brand-rgb))] bg-[rgb(var(--brand-rgb)/0.1)] px-2 py-0.5 rounded-full">
                All amounts in {invoice.currency}
              </span>
            )}
          </div>
          <div className="border border-slate-100 rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 bg-slate-50 px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              <span className="col-span-6">Description</span>
              <span className="col-span-2 text-center">Qty</span>
              <span className="col-span-2 text-right">Unit Price</span>
              <span className="col-span-2 text-right">Total</span>
            </div>
            {(invoice.items || []).map((item, i) => (
              <div key={i} className="grid grid-cols-12 px-3 py-3 border-t border-slate-50 hover:bg-slate-50 transition-colors items-start">
                <span className="col-span-6 text-sm text-slate-700 whitespace-pre-line leading-relaxed pr-2">{item.description}</span>
                <span className="col-span-2 text-sm text-slate-500 text-center">{item.quantity}</span>
                <span className="col-span-2 text-sm text-slate-500 text-right">{fmtMoney(item.unitPrice, invoice.currency)}</span>
                <span className="col-span-2 text-sm font-semibold text-slate-700 text-right">{fmtMoney(item.quantity * item.unitPrice, invoice.currency)}</span>
              </div>
            ))}
            <div className="border-t border-slate-200 px-3 py-3 bg-slate-50 flex justify-end">
              <div className="space-y-1 w-48">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="font-semibold">{fmtMoney(subtotal, invoice.currency)}</span>
                </div>
                <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-1.5">
                  <span>Total</span>
                  <span className="text-[rgb(var(--brand-rgb))]">{fmtMoney(invoice.total ?? subtotal, invoice.currency)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment options */}
        {(invoice.paystackPaymentLink || invoice.bankPaymentInfo?.accountNumber) && (
          <div className="border border-slate-100 rounded-xl p-4 space-y-3">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Payment Options</p>
            {invoice.paystackPaymentLink && (
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Pay Online (Paystack)</p>
                <a href={invoice.paystackPaymentLink} target="_blank" rel="noreferrer"
                  className="text-sm text-blue-600 hover:underline break-all">
                  {invoice.paystackPaymentLink}
                </a>
              </div>
            )}
            {invoice.bankPaymentInfo?.accountNumber && (
              <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-100 text-sm">
                <div><p className="text-xs text-slate-400">Account No.</p><p className="font-semibold text-slate-700">{invoice.bankPaymentInfo.accountNumber}</p></div>
                <div><p className="text-xs text-slate-400">Account Name</p><p className="font-semibold text-slate-700">{invoice.bankPaymentInfo.accountName}</p></div>
                <div><p className="text-xs text-slate-400">Bank</p><p className="font-semibold text-slate-700">{invoice.bankPaymentInfo.bankName}</p></div>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1">Notes</p>
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{invoice.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── INVOICES TAB ─────────────────────────────────────────────────────────────
export function InvoicesTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const { data: invoices, total, page, totalPages, loading, error, setPage, refetch } =
    usePaginated("/crm/invoices");
  const { data: clients }           = usePaginated("/crm/clients", 200);
  const { data: summary, refetch: refetchSummary } =
    useAsync(() => api.get("/crm/invoices/summary"));

  const [filterStatus,  setFilterStatus]  = useState("all");
  const [filterCountry, setFilterCountry] = useState("all");
  const [search,        setSearch]        = useState("");
  const [modal,         setModal]         = useState(null);
  const [sel,           setSel]           = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [actionSaving,  setAS]            = useState(false);

  // Client-side filter (status + country + search all applied locally
  // since invoices are already paginated — swap to server-side if list grows large)
  const shown = (invoices || []).filter(inv => {
    const ms = filterStatus  === "all" || inv.status  === filterStatus;
    const mc = filterCountry === "all" || inv.country === filterCountry;
    const mq = !search ||
      inv.invoiceReference?.toLowerCase().includes(search.toLowerCase()) ||
      inv.clientName?.toLowerCase().includes(search.toLowerCase());
    return ms && mc && mq;
  });

  // Derive unique countries from current invoice list for the filter pills
  const availableCountries = [
    ...new Set((invoices || []).map(inv => inv.country).filter(Boolean)),
  ].sort();

  const doCreate = async form => {
    setSaving(true);
    try   { await api.post("/crm/invoices", form); toast("Invoice created"); setModal(null); refetch(); refetchSummary(); }
    catch (e) { toast(e.message, "error"); }
    finally   { setSaving(false); }
  };

  const doEdit = async form => {
    setSaving(true);
    try   { await api.patch(`/crm/invoices/${sel._id}`, form); toast("Invoice updated"); setModal(null); refetch(); refetchSummary(); }
    catch (e) { toast(e.message, "error"); }
    finally   { setSaving(false); }
  };

  const doDelete = async id => {
    if (!(await confirm({ title: "Delete invoice?", message: "This invoice will be permanently removed.", confirmText: "Delete invoice" }))) return;
    try {
      await api.delete(`/crm/invoices/${id}`);
      toast("Deleted");
      refetch();
      refetchSummary();
      if (modal === "detail") setModal(null);
    }
    catch (e) { toast(e.message, "error"); }
  };

  const doStatusChange = async (id, status) => {
    setAS(true);
    try {
      await api.patch(`/crm/invoices/${id}/status`, { status });
      toast(`Marked as ${status}`);
      refetch();
      refetchSummary();
      if (sel?._id === id) {
        const updated = await api.get(`/crm/invoices/${id}`);
        setSel(updated);
      }
    }
    catch (e) { toast(e.message, "error"); }
    finally   { setAS(false); }
  };

  const doSend = async (id, dto) => {
    setSaving(true);
    const endpoint = modal === "remind"
      ? `/crm/invoices/${id}/remind`
      : `/crm/invoices/${id}/send`;
    try   { await api.post(endpoint, dto); toast(modal === "remind" ? "Reminder sent!" : "Invoice sent!"); setModal("detail"); refetch(); refetchSummary(); }
    catch (e) { toast(e.message, "error"); }
    finally   { setSaving(false); }
  };

  const openDetail = async inv => {
    try   { const full = await api.get(`/crm/invoices/${inv._id}`); setSel(full); setModal("detail"); }
    catch (e) { toast(e.message, "error"); }
  };

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Invoices</h1>
        <Btn variant="primary" onClick={() => setModal("create")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 4v16m8-8H4" /></svg>
          New Invoice
        </Btn>
      </div>

      {/* ── Summary cards ── */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "Draft",   val: summary.draft,                      color: "bg-slate-400"  },
            { label: "Sent",    val: summary.sent,                       color: "bg-blue-500"   },
            { label: "Paid",    val: summary.paid,                       color: "bg-emerald-500" },
            { label: "Overdue", val: summary.overdue,                    color: "bg-rose-500"   },
            { label: "Revenue", val: fmtMoney(summary.totalRevenue),     color: "bg-[rgb(var(--brand-rgb))]"  },
          ].map(s => (
            <Card key={s.label} className="p-4 flex items-center gap-3">
              <div className={`w-2.5 h-8 rounded-full flex-shrink-0 ${s.color}`} />
              <div>
                <p className="text-base font-bold text-slate-800 leading-tight">{s.val}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">{s.label}</p>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <Card className="p-4 space-y-3">
        {/* Search */}
        <div className="relative">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"
            className="absolute left-3 top-1/2 -translate-y-1/2">
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by reference or client name…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
          />
        </div>

        {/* Status pills */}
        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Status</p>
          <div className="flex gap-2 flex-wrap">
            {INV_STATUSES.map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize
                  ${filterStatus === s ? "bg-[rgb(var(--brand-rgb))] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Country pills — only shown when invoices with countries exist */}
        {availableCountries.length > 0 && (
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Country</p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFilterCountry("all")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all
                  ${filterCountry === "all" ? "bg-[rgb(var(--brand-rgb))] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                All Countries
              </button>
              {availableCountries.map(c => {
                const info = COUNTRIES.find(x => x.value === c);
                return (
                  <button key={c} onClick={() => setFilterCountry(c)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all
                      ${filterCountry === c ? "bg-[rgb(var(--brand-rgb))] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    {info ? info.label : c}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Active filter summary + clear */}
        {(filterStatus !== "all" || filterCountry !== "all" || search) && (
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <span className="text-[11px] text-slate-400">Filtering by:</span>
            {filterStatus !== "all" && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[rgb(var(--brand-rgb)/0.1)] text-[rgb(var(--brand-rgb))] capitalize">
                {filterStatus}
              </span>
            )}
            {filterCountry !== "all" && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[rgb(var(--brand-rgb)/0.1)] text-[rgb(var(--brand-rgb))]">
                {COUNTRIES.find(x => x.value === filterCountry)?.label || filterCountry}
              </span>
            )}
            {search && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                "{search}"
              </span>
            )}
            <button
              onClick={() => { setFilterStatus("all"); setFilterCountry("all"); setSearch(""); }}
              className="text-[11px] text-slate-400 hover:text-rose-500 font-bold transition-colors ml-1">
              ✕ Clear
            </button>
          </div>
        )}
      </Card>

      {error   && <ApiError msg={error} onRetry={refetch} />}
      {loading && <Spinner full />}

      {!loading && !error && (
        <>
          {!shown.length ? (
            <Empty
              icon="invoice"
              title="No invoices found"
              sub={search || filterStatus !== "all" || filterCountry !== "all"
                ? "Try adjusting your filters"
                : "Create your first invoice"}
              action={!search && filterStatus === "all" && filterCountry === "all" && (
                <Btn variant="primary" onClick={() => setModal("create")}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 4v16m8-8H4" /></svg>
                  New Invoice
                </Btn>
              )}
            />
          ) : (
            <div className="space-y-2">
              {shown.map(inv => {
                const cInfo = COUNTRIES.find(x => x.value === inv.country);
                return (
                  <Card key={inv._id} className="p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        inv.status === "paid"    ? "bg-emerald-500" :
                        inv.status === "overdue" ? "bg-rose-500"    :
                        inv.status === "sent"    ? "bg-blue-500"    : "bg-slate-300"}`} />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <p className="text-sm font-bold text-slate-800">{inv.invoiceReference}</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${INV_STATUS_COLORS[inv.status] || "bg-slate-100 text-slate-500"}`}>
                            {inv.status}
                          </span>
                          {inv.country && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
                              {cInfo ? cInfo.label : inv.country}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                          <span className="flex items-center gap-1">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                            {inv.clientName}
                          </span>
                          {inv.destination && <span>→ {inv.destination}</span>}
                          {inv.tripDates   && <span>{inv.tripDates}</span>}
                          <span className="flex items-center gap-1">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            {new Date(inv.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                          </span>
                        </div>
                      </div>

                      <div className="text-right mr-2">
                        <p className={`text-base font-bold ${
                          inv.status === "paid"    ? "text-emerald-600" :
                          inv.status === "overdue" ? "text-rose-600"    : "text-slate-800"}`}>
                          {fmtMoney(inv.total, inv.currency)}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {inv.currency || "USD"} · {(inv.items || []).length} item{inv.items?.length !== 1 ? "s" : ""}
                        </p>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <Btn variant="secondary" size="sm" onClick={() => openDetail(inv)}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                          View
                        </Btn>
                        {inv.status !== "paid" && (
                          <Btn variant="success" size="sm"
                            onClick={() => doStatusChange(inv._id, "paid")}
                            loading={actionSaving && sel?._id === inv._id}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 13l4 4L19 7" /></svg>
                            Paid
                          </Btn>
                        )}
                        <Btn variant="ghost" size="sm" onClick={() => { setSel(inv); setModal("edit"); }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </Btn>
                        <Btn variant="danger" size="sm" onClick={() => doDelete(inv._id)}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </Btn>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
          <Pages page={page} totalPages={totalPages} total={total} onPage={setPage} loading={loading} />
        </>
      )}

      {/* ── MODALS ── */}
      {modal === "create" && (
        <Modal title="New Invoice" onClose={() => setModal(null)} wide>
          <InvoiceForm clients={clients || []} onSubmit={doCreate} onClose={() => setModal(null)} saving={saving} />
        </Modal>
      )}

      {modal === "edit" && sel && (
        <Modal title={`Edit ${sel.invoiceReference}`} onClose={() => setModal(null)} wide>
          <InvoiceForm initial={sel} clients={clients || []} onSubmit={doEdit} onClose={() => setModal(null)} saving={saving} />
        </Modal>
      )}

      {(modal === "send" || modal === "remind") && sel && (
        <Modal
          title={modal === "remind" ? "Send Payment Reminder" : "Send Invoice"}
          onClose={() => setModal("detail")}>
          <SendModal
            invoice={sel} mode={modal} saving={saving}
            onSend={dto => doSend(sel._id, dto)}
            onClose={() => setModal("detail")} />
        </Modal>
      )}

      {modal === "detail" && sel && (
        <div
          className="fixed inset-0 z-50 flex"
          style={{ background: "rgba(15,23,42,.55)", backdropFilter: "blur(4px)" }}>
          <div
            className="ml-auto w-full max-w-2xl bg-white h-full flex flex-col shadow-2xl"
            style={{ animation: "slideIn .25s ease" }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Invoice Detail</span>
              <button
                onClick={() => setModal(null)}
                className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <InvoiceDetailPanel
                invoice={sel}
                actionSaving={actionSaving}
                onClose={() => setModal(null)}
                onEdit={() => setModal("edit")}
                onSend={() => setModal("send")}
                onRemind={() => setModal("remind")}
                onStatusChange={status => doStatusChange(sel._id, status)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}